import {
  DailyCargoModel, DeadlineFilterModel, ManagedUserModel, ParcelFilter, ReactivateParcelsModel,
  ContactModel,
  FilterModel,
  ParcelDetailModel,
  ParcelListModel,
  BookParcelModel,
  ConsolidatedBookParcelModel,
  DeleteParcelsModel,
  ExcelListModel,
  PdfListModel,
  ScanParcelModel,
  ProblemReportModel,
  UpdateEventModel,
  SearchContactModel,
  SearchAddressModel,
  CreateAddressModel,
  ResetPasswordModel,
  Report,
  ExportProof,
  Deadline,
  CreateCMRModel,
  PersonalSettings,
  ProblemReportFilter,
  Rac_Validation,
  SetProblemReportMeasure,
  ProfileModel,
  AdhocDrive,
  AdhocDriveFilterModel,
  DailyCargoDriver,
  PreCarriageFilterModel,
  LinehaulRoadFilterModel,
  Customer,
  Country,
  User,
  UserScannerOption,
  UserScannerProfile
} from '@/webApi/DataModels'
// import the config
import config from '../config'
import axios, { AxiosPromise, AxiosRequestConfig, Method, ResponseType } from 'axios'
import Router from '../router'
import store from '../store'
import linehaulRoad from '@/vuexModules/linehaulRoad'

export interface HttpResponse {
  data: any;
  ok: boolean;
  status: number;
  statusText: string;
  headers: Function;
  text(): string;
  json(): any;
  blob(): Blob;
}

export interface HttpOptions {
  url?: string;
  method?: string;
  body?: any;
  params?: any;
  headers?: any;
  before?(request: any): any;
  progress?(event: any): any;
  credentials?: boolean;
  emulateHTTP?: boolean;
  emulateJSON?: boolean;
}

function createRequest (url: string, data: any = {}, method: Method = 'POST', headers:any = {}, responseType:ResponseType = 'json'):AxiosPromise<Response> {
  var options:AxiosRequestConfig = {
    method,
    url: url,
    baseURL: config.get_API_URL(),
    headers,
    data,
    responseType
  }
  store.dispatch('auth/initialize')
  if (store.getters['auth/isAuthenticated']()) {
    options.headers.Authorization = store.getters['auth/authHeader']
  } else if (
    url.indexOf('/Anonymous_Tracking/search_Parcels') === -1 &&
    url.indexOf('/Gps/get_Current_Gps_Position') === -1 &&
    url.indexOf('/Account/confirm_Email') === -1 &&
    url.indexOf('/Account/reset_Password') === -1) {
    Router.push('/login')
  }
  return axios(options)
}

// --- Backend V2 token exchange ---
let v2Token: string | null = null
let v2TokenPromise: Promise<string> | null = null

export function clearV2Token (): void {
  v2Token = null
  v2TokenPromise = null
}

function getV2Token (): Promise<string> {
  if (v2Token) return Promise.resolve(v2Token)
  if (v2TokenPromise) return v2TokenPromise

  store.dispatch('auth/initialize')
  const legacyToken = store.state.auth.token

  v2TokenPromise = axios.post(config.get_API_V2_URL() + '/token/exchange', {
    legacy_token: legacyToken
  }).then((result) => {
    v2Token = result.data.access_token
    v2TokenPromise = null
    return v2Token!
  }).catch((err) => {
    v2TokenPromise = null
    throw err
  })
  return v2TokenPromise
}

function createV2Request (url: string, data: any = {}, method: Method = 'POST', headers: any = {}, responseType: ResponseType = 'json'): Promise<any> {
  return getV2Token().then((token) => {
    var options: AxiosRequestConfig = {
      method,
      url: url,
      baseURL: config.get_API_V2_URL(),
      headers: { ...headers, Authorization: 'Bearer ' + token },
      data,
      responseType
    }
    return axios(options)
  })
}

// --- 401 Refresh Token Interceptor ---
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = []

function processQueue (error: any, token: string | null): void {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

axios.interceptors.response.use(
  response => response,
  error => {
    const originalRequest = error.config

    // Only handle 401s; skip if this is already a retry or a token endpoint request
    const url = originalRequest.url || ''
    const isTokenEndpoint = url.indexOf('Token/refresh') !== -1 ||
      (url.endsWith('Token') || url.endsWith('Token/'))
    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry ||
      isTokenEndpoint
    ) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // Another refresh is in-flight — queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then((token: string) => {
        originalRequest.headers.Authorization = 'Bearer ' + token
        originalRequest._retry = true
        return axios(originalRequest)
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    return store.dispatch('auth/refreshAccessToken')
      .then((newToken: string) => {
        processQueue(null, newToken)
        originalRequest.headers.Authorization = 'Bearer ' + newToken
        return axios(originalRequest)
      })
      .catch((err: any) => {
        processQueue(err, null)
        clearV2Token()
        store.dispatch('auth/logout')
        return Promise.reject(err)
      })
      .finally(() => {
        isRefreshing = false
      })
  }
)

export default {

  // *********************** Dashboard Controller ***********************************
  dashboard: {
    // get the list of parcel based on the filrer settings
    get_Dashboard_Report: function (filter:FilterModel, groupByCountry:boolean):AxiosPromise<Response> {
      return createRequest('api/Dashboard/get_Dashboard_Report?group_by_country=' + groupByCountry, filter)
    }
  },
  // *********************** Parcel Controller ***********************************
  // Parcel Controller Functions below
  parcel: {
    // get the list of parcel based on the filrer settings
    get_Parcel_List_By_Filter: function (filter:FilterModel):AxiosPromise<Response> {
      return createRequest('api/Parcel/get_Parcel_List_By_Filter/', filter)
    },

    // get the parcel details for the ID
    get_Parcel_Details_By_ID: function (id:number):AxiosPromise<Response> {
      return createRequest('api/Parcel/get_Parcel_Details_By_ID?id=' + id, null, 'GET')
    },

    // Get the parcel timeline
    get_Parcel_Timeline: function (id: number):AxiosPromise<Response> {
      return createRequest('api/Parcel/get_Parcel_Timeline?parcel_id=' + id, null, 'GET')
    },

    // Get the parcel by reference
    search_Parcel_By_Reference: function (reference: string):AxiosPromise<Response> {
      return createRequest('api/Parcel/search_Parcel_By_References?reference=' + encodeURIComponent(reference), null, 'GET')
    },

    // Update the parcel
    update_Parcel: function (parcelDetails: ParcelDetailModel):AxiosPromise<Response> {
      return createRequest('api/Parcel/update_Parcel/', parcelDetails, 'POST', {}, 'arraybuffer')
    },

    // Update the parcel
    update_Parcel_List: function (updateParcelListModel: ParcelListModel):AxiosPromise<any> {
      return createRequest('api/Parcel/update_Parcel_List/', updateParcelListModel)
    },

    // Book a parcel
    book_Parcel: function (bookParcelModel: BookParcelModel):AxiosPromise<any> {
      return createRequest('api/Parcel/book_Parcel/', bookParcelModel)
    },

    // Consolidated Booking
    book_Consolidated_Parcel: function (bookParcelModel: ConsolidatedBookParcelModel):AxiosPromise<any> {
      return createRequest('api/Parcel/book_Consolidated_Parcel/', bookParcelModel)
    },

    // verifies parcels in an excel file. The file is uploaded in formData under files. 1 file per call.
    verify_Parcels_in_Excel_File: function (formData: FormData):AxiosPromise<any> {
      return createRequest('api/Parcel/verify_Parcels_in_Excel_File/', formData)
    },

    // Book parcels with excel file. The file is uploaded in formData under files. 1 file per call.
    book_Parcels_With_Excel_File: function (formData: FormData):AxiosPromise<any> {
      return createRequest('api/Parcel/book_Parcels_With_Excel_File/', formData)
    },

    // Update parcels with excel file. The file is uploaded in formData under files. 1 file per call.
    update_Parcels_With_Excel_File: function (formData: FormData):AxiosPromise<any> {
      return createRequest('api/Parcel/book_Parcels_With_Excel_File/', formData)
    },

    // Delete parcels in the list
    delete_Parcel_List: function (deleteParcelsModel: DeleteParcelsModel):AxiosPromise<any> {
      return createRequest('api/Parcel/delete_Parcel_List/', deleteParcelsModel, 'POST')
    },

    // Reactivate parcels in the list
    reactivate_Parcel_List: function (reactivateParcelsModel: ReactivateParcelsModel):AxiosPromise<any> {
      return createRequest('api/Parcel/reactivate_Parcel_List/', reactivateParcelsModel)
    },

    // *********************** Excel Export **********************************
    // Get the parcel list as excel
    download_Parcel_List_XLSX: function (excelList: ExcelListModel):AxiosPromise<any> {
      return createRequest('api/Parcel/download_Parcel_List_XLSX/', excelList, 'POST', {}, 'arraybuffer')
    },
    // Get the export proof list as excel
    download_Export_Proof_List_XLSX: function (excelList: ExcelListModel):AxiosPromise<any> {
      return createRequest('api/Parcel/download_Export_Proof_List_XLSX/', excelList, 'POST', {}, 'arraybuffer')
    },

    // *********************** Scan Parcel **********************************
    // Adds a new scan event to parcels
    scan_Parcels: function (scanParcelModel: ScanParcelModel):AxiosPromise<any> {
      return createRequest('api/Parcel/scan_Parcels/', scanParcelModel)
    },

    // verifies scans in an excel file. The file is uploaded in formData under files. 1 file per call.
    verify_Scans_in_Excel_File: function (formData: FormData):AxiosPromise<any> {
      return createRequest('api/Parcel/verify_Scans_in_Excel_File/', formData)
    },

    // scans parcels in an excel file. The file is uploaded in formData under files. 1 file per call.
    scan_Parcels_With_Excel_File: function (formData: FormData):AxiosPromise<any> {
      return createRequest('api/Parcel/scan_Parcels_With_Excel_File/', formData)
    },

    // *********************** Get Countere Number for parcel **********************************
    // Adds a new scan event to the parcel
    get_Counter_Number: function (parcelToBook: BookParcelModel):AxiosPromise<Response> {
      return createRequest('api/Counter_Number/generate_Counter_Number/', parcelToBook)
    }
  },
  deadline: {
    // get the list of available deadlines
    get_Deadline_List: function (deadlineModel: DeadlineFilterModel):AxiosPromise<Response> {
      return createRequest('api/Deadline/get_Deadline_List/', deadlineModel, 'POST')
    },
    get_Deadline_By_ID: function (deadlineID: number):AxiosPromise<any> {
      return createRequest('api/Deadline/get_Deadline_By_ID?deadline_id=' + deadlineID, {}, 'GET')
    },
    // save the new deadline in case of insert or edit
    save_Deadline: function (deadlineModel: Deadline):AxiosPromise<any> {
      return createRequest('api/Deadline/save_Deadline/', deadlineModel)
    }
  },
  adhocDrive: {
    // get the list of available adhocDrives
    get_AdhocDrive_List: function (adhocDriveModel: AdhocDriveFilterModel):AxiosPromise<Response> {
      return createRequest('api/Adhoc_Drive/get_Adhoc_Drive_List/', adhocDriveModel, 'POST')
    },
    get_AdhocDrive_By_ID: function (adhocDriveId: number):AxiosPromise<any> {
      return createRequest('api/Adhoc_Drive/get_Adhoc_Drive_By_Id?adhoc_drive_id=' + adhocDriveId, {}, 'GET')
    },
    // save the new adhocDrive in case of insert or edit
    save_AdhocDrive: function (adhocDrive: AdhocDrive):AxiosPromise<any> {
      return createRequest('api/Adhoc_Drive/save_Adhoc_Drive/', adhocDrive)
    },
    update_AdhocDrive: function (adhocDrive: any):AxiosPromise<any> {
      return createRequest('api/Adhoc_Drive/update_Adhoc_Drive/', adhocDrive)
    }
  },
  linehaulRoad: {
    get_LinehaulRoad_By_ID: function (linehaulRoadId: number):AxiosPromise<Response> {
      return createRequest('api/LinehaulRoad/get_LinehaulRoad_By_Id?linehauroad_id=' + linehaulRoadId, {}, 'GET')
    },
    get_LinehaulRoad_List: function (linehaulRoadModel: LinehaulRoadFilterModel):AxiosPromise<Response> {
      return createRequest('api/LinehaulRoad/get_LinehaulRoad_List/', linehaulRoadModel, 'POST')
    },
    get_LinehaulRoad_Options: function ():AxiosPromise<Response> {
      return createRequest('api/LinehaulRoad/get_LinehaulRoad_Options', {}, 'GET')
    },
    // save the new linehaul road in case of insert or edit
    save_LinehaulRoad: function (linehaulRoad: any):AxiosPromise<any> {
      return createRequest('api/LinehaulRoad/save_LinehaulRoad/', linehaulRoad)
    },
    update_LinehaulRoad: function (linehaulRoad: any):AxiosPromise<any> {
      return createRequest('api/LinehaulRoad/update_LinehaulRoad/', linehaulRoad)
    }
  },
  // *********************** Daily Cargo **********************************
  daily_cargo: {
    get_Flight_Routes: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Flight_Routes', {}, 'GET')
    },
    get_Daily_Cargo_Data: function (flightId: number):AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Data?flight_id=' + flightId, {}, 'POST')
    },
    get_Daily_Cargo_Driver_By_Flight_Id: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Driver_By_Flight_Id', {}, 'GET')
    },
    get_Daily_Cargo_Driver: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Driver', {}, 'GET')
    },
    get_Daily_Cargo_Driver_By_Id: function (driverId: number):AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Driver_By_Id?driver_id=' + driverId, {}, 'GET')
    },
    get_Daily_Cargo_Identity_Check_Sheet_List: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Identity_Check_Sheet_List', {}, 'GET')
    },
    get_Daily_Cargo_Flights: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Flights', {}, 'GET')
    },
    save_Daily_Cargo: function (dailyCargoModel: DailyCargoModel):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/save_Daily_Cargo/', dailyCargoModel)
    },
    save_Daily_Cargo_Flight: function (dailyCargoFlightsModel: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/save_Daily_Cargo_Flights/', dailyCargoFlightsModel)
    },
    save_Daily_Cargo_Default_Flight: function (dailyCargoFlightsModel: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/save_Daily_Cargo_Default_Flight/', dailyCargoFlightsModel)
    },
    save_Daily_Cargo_Seal_Number: function (dailyCargoSealNumberModel: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/save_Daily_Cargo_Seal_Number/', dailyCargoSealNumberModel)
    },
    save_Daily_Cargo_Rac_Data: function (dailyCargoRacDataModel: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/save_Daily_Cargo_Rac_Data/', dailyCargoRacDataModel)
    },
    get_Daily_Cargo_Rac_Validation: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Rac_Validation', {}, 'GET')
    },
    get_Daily_Cargo_Rac_Number: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Daily_Cargo_Rac_Number', {}, 'GET')
    },
    save_Rac_Validation: function (racValidationData: Rac_Validation):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/save_Rac_Validation/', racValidationData)
    },
    get_Generated_Manifest: function ():AxiosPromise<Response> {
      return createRequest('api/Daily_Cargo/get_Generated_Manifest', {}, 'GET')
    },
    download_Manifest_PDF_New: function (dailyCargoData: DailyCargoModel):AxiosPromise<any> {
      return createV2Request('api/Daily_Cargo/download_Manifest_PDF_New/', dailyCargoData, 'POST', {})
    },
    download_Identity_Check_Sheet: function (flightId: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/download_Identity_Check_Sheet?flightId=' + flightId, {}, 'GET')
    },
    download_Summary_PDF: function (dailyCargoData: DailyCargoModel):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/download_Summary_PDF/', dailyCargoData, 'POST', {})
    },
    save_Daily_Cargo_Driver: function (dailyCargoDriver: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/save_Daily_Cargo_Driver/', dailyCargoDriver)
    },
    delete_Daily_Cargo_Driver: function (dailyCargoDriver: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/delete_Daily_Cargo/', dailyCargoDriver)
    },
    delete_Rac_Number: function (racNumber: any):AxiosPromise<any> {
      return createRequest('api/Daily_Cargo/delete_Rac_Number/', racNumber)
    }
  },
  scan: {
    // Adds a new scan event to the parcel
    scan_Parcel: function (scanParcelModel: ScanParcelModel):AxiosPromise<any> {
      return createRequest('api/Scan/scan_Parcel/', scanParcelModel)
    }
  },
  profile: {
    // Adds a new scan event to the parcel
    get_Profile: function (profileModel: ProfileModel):AxiosPromise<any> {
      return createRequest('api/Profile/get_Profiler_By_Filter/', profileModel)
    }
  },
  // *********************** Reference Controller ***********************************
  // Reference Controller Functions below
  reference: {
    // Checks if the counternumber is valid
    check_Booking_Counternumber_is_Valid: function (bookParcelModel: BookParcelModel):AxiosPromise<Response> {
      return createRequest('api/Reference/check_Booking_Counternumber_is_Valid/', bookParcelModel)
    },

    // Get the reference types
    get_Reference_Type_List: function ():AxiosPromise<Response> {
      return createRequest('api/Reference/get_Reference_Type_List/', null, 'GET')
    },
    // Get Reference Manifest
    get_Reference_Manifest: function ():AxiosPromise<Response> {
      return createRequest('api/Reference/get_Reference_Manifest/', null, 'GET')
    }
  },

  drop_cost: {
    get_Drop_Cost_Report: function (parcelFilter:ParcelFilter):AxiosPromise<Response> {
      return createRequest('api/Drop_Cost/get_Drop_Cost_Report/', parcelFilter)
    }
  },

  anonymous_tracking: {
    search_Parcels: function (referenceText:string, referenceTypeID:number, customerID:number):Promise<any> {
      var searchModel = { Reference_Text: referenceText, Reference_Type_ID: referenceTypeID, Customer_ID: customerID }
      return createRequest('api/Anonymous_Tracking/search_Parcels', searchModel)
    },

    // *********************** GPS Coordinates **********************************
    // get the parcel gps coordinates if available
    get_Current_Gps_Position_By_Parcel_Id: function (id:number):AxiosPromise<Response> {
      return createRequest('api/Gps/get_Current_Gps_Position?parcel_id=' + id, null, 'GET')
    }
  },
  // *********************** Problem Report Controller ***********************************
  // Problem Report Controller Functions below
  problem_report: {
    create_Problem_Report_For_Parcel_List: function (problemReportModel: ProblemReportModel):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/create_Problem_Report_For_Parcel_List/', problemReportModel)
    },

    // Checks if SMS sending is possible for the parcel list
    check_If_SMS_Possible_For_Problem_Report: function (parcelIDs:number[]):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/check_If_SMS_Possible_For_Problem_Report/', parcelIDs)
    },

    // Get the problem  responsible types
    get_Problem_Responsible_Type_List: function ():AxiosPromise<Response> {
      return createRequest('api/Problem_Report/get_Problem_Responsible_Type_List', null, 'GET')
    },

    // get the problem types for the responsible type ID
    get_Problem_Type_List: function (id: number):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/get_Problem_Type_List?problem_responsible_type_id=' + id.toString(10), null, 'GET')
    },

    // get the problem types for the responsible type ID
    get_Problem_Responsible_List: function (id: number):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/get_Problem_Responsible_List_By_Responsible_Type_ID?problem_responsible_type_id=' + id.toString(10), null, 'GET')
    },
    // get the possible problem report measures
    getProblemReportMeasures: function ():AxiosPromise<Response> {
      return createRequest('api/Problem_Report/problemReportMeasures', null, 'GET')
    },

    // get the problem reports with additional information about the measures
    getProblemReportsWithMeasures: function (problemReportFilter:ProblemReportFilter):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/problemReportsWithMeasures', problemReportFilter)
    },

    // updates the problem report with a measure
    updateProblemReportsWithMeasures: function (problemReportMeasure:SetProblemReportMeasure):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/updateProblemReportsWithMeasures', problemReportMeasure)
    },

    // Deactivate problem reports of the selected parcels
    deactivate_Problem_Reports_In_Parcel_List: function (parcelIDs: number[]):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/deactivate_Problem_Reports_In_Parcel_List/', parcelIDs)
    },

    // Deactivate problem reports
    deactivate_Problem_Report_By_ID: function (problemReportID):AxiosPromise<Response> {
      return createRequest('api/Problem_Report/deactivate_Problem_Report_By_ID?problem_report_id=' + problemReportID)
    }
  },
  // *********************** Events Controller **********************************
  event: {
    // Update Event
    update_event: function (updateEventModel: UpdateEventModel):AxiosPromise<Response> {
      return createRequest('api/Event/update_Event/', updateEventModel)
    },
    // Activate Events
    activate_Event: function (eventIDList: number[]):AxiosPromise<Response> {
      return createRequest('api/Event/activate_Event/', eventIDList)
    },
    // Deactivate Events
    deactivate_Event: function (eventIDList: number[]):AxiosPromise<Response> {
      return createRequest('api/Event/deactivate_Event/', eventIDList)
    },
    deactivate_Current_Event_By_Parcel_ID_List: function (parcelIDs:number[]):AxiosPromise<Response> {
      return createRequest('api/Event/deactivate_Current_Event_By_Parcel_ID_List', parcelIDs)
    },
    deactivate_Event_By_Event_Info_ID_and_Parcel_ID_List: function (eventInfoID:number, parcelIDs:number[]):AxiosPromise<Response> {
      return createRequest('api/Event/deactivate_Event_By_Event_Info_ID_and_Parcel_ID_List', { Event_Info_ID: eventInfoID, Parcel_ID_List: parcelIDs })
    }
  },
  // *********************** Report Controller ***********************************
  report: {
    // get the column setup for creating / editing reports
    get_Report_Column_Setup: function ():AxiosPromise<Response> {
      return createRequest('api/Report/get_Report_Column_Setup', {}, 'GET')
    },
    // get the list of reports available
    get_Report_Template_List: function ():AxiosPromise<Response> {
      return createRequest('api/Report/get_Report_Template_List/', {}, 'GET')
    },
    // get the report description (report layout and default reportFilter settings)
    get_Report_Template: function (reportID: number):AxiosPromise<any> {
      return createRequest('api/Report/get_Report_Template/?report_id=' + reportID, {}, 'GET')
    },
    // save the report layout and reportFilter settings
    save_Report_Template: function (reportTemplateModel: Report):AxiosPromise<any> {
      return createRequest('api/Report/save_Report_Template/', reportTemplateModel)
    },
    // get the list of parcel based on the filrer settings
    execute_Report: function (reportModel: any):AxiosPromise<Response> {
      return createRequest('api/Report/get_Report/', reportModel)
    },
    // delete report and reportFilter settings
    delete_Report: function (report_Id):AxiosPromise<any> {
      return createRequest('api/Report/delete_Report/?report_Id=' + report_Id, {}, 'DELETE')
    }
  },

  // *********************** Export Proof Controller ***********************************
  exportProof: {
    // get the column setup for creating / editing export proofs
    get_Export_Proof_Column_Setup: function ():AxiosPromise<Response> {
      return createRequest('api/Export_Proof/get_Export_Proof_Column_Setup', {}, 'GET')
    },
    // get the list of export proofs available
    get_Export_Proof_Template_List: function ():AxiosPromise<Response> {
      return createRequest('api/Export_Proof/get_Export_Proof_Template_List/', {}, 'GET')
    },
    // get the export proof description (export proof layout and default exportProofFilter settings)
    get_Export_Proof_Template: function (exportProofID: number):AxiosPromise<any> {
      return createRequest('api/Export_Proof/get_Export_Proof_Template?export_proof_id=' + exportProofID, {}, 'GET')
    },
    // save the export proof layout and exportProofFilter settings
    save_Export_Proof_Template: function (exportProofTemplateModel: ExportProof):AxiosPromise<any> {
      return createRequest('api/Export_Proof/save_Export_Proof_Template/', exportProofTemplateModel)
    },
    // get the list of parcel based on the filrer settings
    execute_Export_Proof: function (exportProofModel: any):AxiosPromise<Response> {
      return createRequest('api/Export_Proof/get_Export_Proof/', exportProofModel)
    },
    // Get the export proof list as pdf
    download_Export_Proof_List_PDF: function (pdfList: PdfListModel):AxiosPromise<any> {
      return createRequest('api/Export_Proof/download_Export_Proof_List_PDF/', { export_Proof_XLSX_Model: pdfList, type: 'PDF', language: '' }, 'POST', {})
    },
    send_Export_Proof_Email: function (pdfList: PdfListModel, type: string, language: string):AxiosPromise<any> {
      return createRequest('api/Export_Proof/download_Export_Proof_List_PDF/', { export_Proof_XLSX_Model: pdfList, type: type, language: language }, 'POST', {})
    }
  },

  // *********************** Monitoring Controller ***********************************
  monitoring: {
    // get the report description (report layout and default reportFilter settings)
    get_Migration_Error_Logs: function ():AxiosPromise<Response> {
      return createRequest('api/Monitoring/get_Migration_Error_Logs', {}, 'GET')
    }
  },
  // *********************** News Controller ***********************************
  split_news: {
    // get the internal news
    get_internal_News: function ():AxiosPromise<Response> {
      return createRequest('api/Split_News/get_internal_News', {}, 'GET')
    },
    create_internal_News: function (newInternalNews: object):AxiosPromise<Response> {
      return createRequest('api/Split_News/create_internal_News', newInternalNews, 'POST')
    },
    update_internal_News: function (updatedInternalNews: object):AxiosPromise<Response> {
      return createRequest('api/Split_News/update_internal_News', updatedInternalNews, 'POST')
    }
  },

  // *********************** Country Controller ***********************************
  country: {
    // get the countries
    get_Countries: function (): AxiosPromise<Response> {
      return createRequest('api/Country/get_Country_List', {}, 'GET')
    },
    // create a new country
    create: function (dto: Country): AxiosPromise<Response> {
      return createRequest('api/Country/create_Country', dto, 'POST')
    },
    // update an existing country
    update: function (dto: Country): AxiosPromise<Response> {
      return createRequest('api/Country/update_Country', dto, 'PUT')
    },
    // delete a country by id
    delete: function (id: number): AxiosPromise<Response> {
      return createRequest(`api/Country/delete_Country/${id}`, {}, 'DELETE')
    }
  },

  // *********************** Currency Controller ***********************************
  currency: {
    // get the countries
    get_Currencies: function ():AxiosPromise<Response> {
      return createRequest('api/Currency/get_Currency_List', {}, 'GET')
    }
  },

  // *********************** Service Leg Controller ***********************************
  service_Leg: {
    // get the service legs
    get_Service_Legs: function ():AxiosPromise<Response> {
      return createRequest('api/Service_Leg/get_Service_Leg_List', {}, 'GET')
    }
  },

  // *********************** Customer Controller ***********************************
  customer: {
    // Get all customers
    get_Customers (): AxiosPromise<Response> {
      return createRequest('api/Customer/get_Customer_List', {}, 'GET')
    },

    // Create new customer
    create (customer: Customer): AxiosPromise<Response> {
      return createRequest('api/Customer/create_Customer', customer, 'POST')
    },

    // Update existing customer
    update (customer: Customer): AxiosPromise<Response> {
      return createRequest('api/Customer/update_Customer', customer, 'PUT')
    },

    // Delete customer
    delete (id: number): AxiosPromise<Response> {
      return createRequest(`api/Customer/delete_Customer/${id}`, {}, 'DELETE')
    }
  },

  // *********************** Service Controller ***********************************
  service_level: {
    // get the service levels
    get_Service_Levels: function ():AxiosPromise<Response> {
      return createRequest('api/Service_Level/get_Service_Level_List', {}, 'GET')
    }
  },

  // *********************** Event Info Controller ***********************************
  event_info: {
    // get the event infos
    get_Event_Infos: function ():AxiosPromise<Response> {
      return createRequest('api/Event/get_Event_Info_List', {}, 'GET')
    }
  },

  provider: {
    get_Providers: function ():AxiosPromise<Response> {
      return createRequest('api/Provider/get_Providers', {}, 'GET')
    },
    get_Active_Providers: function ():AxiosPromise<Response> {
      return createRequest('api/Provider/get_Active_Providers', {}, 'GET')
    }
  },

  vehicle: {
    get_Vehicles: function ():AxiosPromise<Response> {
      return createRequest('api/Vehicle/get_Vehicle_List', {}, 'GET')
    }
  },

  preCarriage_route: {
    get_PreCarriage_Routes: function ():AxiosPromise<Response> {
      return createRequest('api/PreCarriage_Route/get_PreCarriage_Route_List', {}, 'GET')
    },
    get_PreCarriage_By_ID: function (preCarriageId: number):AxiosPromise<Response> {
      return createRequest('api/PreCarriage_Route/get_PreCarriage_By_Id?precarriage_id=' + preCarriageId, {}, 'GET')
    },
    get_PreCarriage_List: function (preCarriageModel: PreCarriageFilterModel):AxiosPromise<Response> {
      return createRequest('api/PreCarriage_Route/get_PreCarriage_List/', preCarriageModel, 'POST')
    },
    // save the new precarriage in case of insert or edit
    save_PreCarriage_Routes: function (preCarriage: any):AxiosPromise<any> {
      return createRequest('api/PreCarriage_Route/save_PreCarriage_Records/', preCarriage)
    }
  },

  tag: {
    get_Tags: function ():AxiosPromise<Response> {
      return createRequest('api/Tag/get_Tags', {}, 'GET')
    }
  },
  // *********************** Contact Controller ***********************************
  contact: {
    // search for addresses
    get_Contact_List: function (searchContactModel: SearchContactModel):AxiosPromise<Response> {
      return createRequest('api/Contact/get_Contact_List/', searchContactModel)
    },
    update_Contact: function (contactModel: ContactModel):AxiosPromise<Response> {
      return createRequest('api/Contact/update_Contact/', contactModel)
    },
    create_Contact: function (contactModel: ContactModel):AxiosPromise<Response> {
      return createRequest('api/Contact/create_Contact/', contactModel)
    },
    delete_Contact: function (contactID: number):AxiosPromise<Response> {
      return createRequest('api/Contact/delete_Contact/', contactID)
    }
  },
  // *********************** Address Controller ***********************************
  address: {
    // search for addresses
    get_Address_List: function (searchAddressModel: SearchAddressModel):AxiosPromise<Response> {
      return createRequest('api/Address/get_Address_List/', searchAddressModel)
    },
    // get the address by ID
    get_Address: function (addressID: number):AxiosPromise<Response> {
      return createRequest('api/Address/get_Address/', addressID)
    },
    // get the address by ID
    create_Address: function (addressModel: CreateAddressModel):AxiosPromise<Response> {
      return createRequest('api/Address/create_Address/', addressModel)
    }
  },

  // *********************** Account Controller ***********************************
  account: {
    create_User: function (userModel: ManagedUserModel):AxiosPromise<Response> {
      return createRequest('api/Account/create_User/', userModel)
    },

    create_Users_With_Excel_File: function (formData: FormData):AxiosPromise<Response> {
      return createRequest('api/Account/create_Users_With_Excel_File/', formData)
    },

    update_User: function (userModel: ManagedUserModel):AxiosPromise<Response> {
      return createRequest('api/Account/update_User/', userModel)
    },

    get_User_List: function ():AxiosPromise<Response> {
      return createRequest('api/Account/get_User_List/', null, 'GET')
    },

    get_Active_User_List: function ():AxiosPromise<Response> {
      return createRequest('api/Account/get_Active_User_List/', null, 'GET')
    },

    get_Roles: function ():AxiosPromise<Response> {
      return createRequest('api/Account/get_Roles', null, 'GET')
    },

    lockout_User: function (permissionID:string):AxiosPromise<Response> {
      return createRequest('api/Account/lockout_User?permissions_user_id=' + permissionID)
    },

    unlock_User: function (permissionID:string):AxiosPromise<Response> {
      return createRequest('api/Account/unlock_User?permissions_user_id=' + permissionID)
    },

    confirm_Email: function (userId: string, code: string):AxiosPromise<Response> {
      var userIdEncoded = encodeURIComponent(userId)
      var codeEncoded = encodeURIComponent(code)
      return createRequest('api/Account/confirm_Email?user_id=' + userIdEncoded + '&token=' + codeEncoded, null, 'GET')
    },

    reset_Password: function (resetPasswordModel: ResetPasswordModel):AxiosPromise<Response> {
      return createRequest('api/Account/reset_Password', resetPasswordModel)
    },

    send_Reset_Password_Mail: function (email: string):AxiosPromise<Response> {
      return createRequest('api/Account/send_Reset_Password_Mail?email=' + email, null, 'GET')
    },

    get_User_Identity: function ():AxiosPromise<Response> {
      return createRequest('api/Account/get_User_Identity_Model/', {}, 'GET')
    },

    downloadUserQrCode: function (userId: number):AxiosPromise<Response> {
      return createRequest('api/User/downloadUserQrCode/' + userId, null, 'GET', {}, 'arraybuffer')
    }
  },
  // *********************** Personal Settings Controller ***********************************
  personalSettings: {
    // get the personal settings
    get_Personal_Settings: function ():AxiosPromise<Response> {
      return createRequest('api/Personal_Settings/get_Personal_Settings/')
    },

    // Save the personal settings
    save_Personal_Settings: function (personalSettingsModel:PersonalSettings):AxiosPromise<Response> {
      return createRequest('api/Personal_Settings/save_Personal_Settings/?json_settings=' + encodeURIComponent(JSON.stringify(personalSettingsModel)))
    }
  },

  document: {
    download_Book_With_File_Template: function ():AxiosPromise<Response> {
      return createRequest('api/Document/download_Book_With_File_Template/', null, 'GET', {}, 'arraybuffer')
    },

    download_Update_With_File_Template: function ():AxiosPromise<Response> {
      return createRequest('api/Document/download_Update_With_File_Template/', null, 'GET', {}, 'arraybuffer')
    },

    download_Scan_With_File_Template: function ():AxiosPromise<Response> {
      return createRequest('api/Document/download_Scan_With_File_Template/', null, 'GET', {}, 'arraybuffer')
    },

    // Save the personal settings
    create_CMR_Documents: function (createCMRModel:CreateCMRModel): AxiosPromise<Response> {
      return createRequest('api/Document/create_CMR_Documents/', createCMRModel, 'POST', {}, 'arraybuffer')
    },

    // upload document
    upload_Document: function (formData: FormData):AxiosPromise<Response> {
      return createRequest('api/Document/upload_Document/', formData, 'POST', {
        'Content-Type': 'multipart/form-data'
      })
    },

    // Save the personal settings
    download_Document: function (documentID: number) {
      return createRequest('api/Document/download_Document?document_id=' + documentID, null, 'GET', {}, 'arraybuffer')
    }
  },
  pudo: {
    get_Expired_Parcels: function (parcelFilter:ParcelFilter):AxiosPromise<Response> {
      return createRequest('api/PUDO_ExpiryController/get_Expired_Parcels', parcelFilter)
    },
    get_Expiry_Options: function (parcelFilter:ParcelFilter):AxiosPromise<Response> {
      return createRequest('api/PUDO_ExpiryController/get_Expiry_Options', parcelFilter)
    },
    save_PUDO_Expiry: function (parcelIDs:number[], actionID:number):AxiosPromise<Response> {
      return createRequest('api/PUDO_ExpiryController/save_PUDO_Expiry', { Parcel_IDs: parcelIDs, PUDO_Expiry_ID: actionID })
    },
    get_PUDO_Expiry_History: function (parcelFilter:ParcelFilter):AxiosPromise<Response> {
      return createRequest('api/PUDO_ExpiryController/get_PUDO_Expiry_History', parcelFilter)
    }
  },
  // ------------------- User -------------------
  user: {
    get_Users: function (): AxiosPromise<Response> {
      return createRequest('api/User/get_User_List', {}, 'GET')
    },
    create: function (dto: User): AxiosPromise<Response> {
      return createRequest('api/User/create_User', dto, 'POST')
    },
    update: function (dto: User): AxiosPromise<Response> {
      return createRequest('api/User/update_User', dto, 'PUT')
    },
    delete: function (id: number): AxiosPromise<Response> {
      return createRequest(`api/User/delete_User/${id}`, {}, 'DELETE')
    }
  },

  // ------------------- User Scanner Profile -------------------
  userScannerProfile: {
    get_By_User: function (userId: number): AxiosPromise<Response> {
      return createRequest(`api/UserScannerProfile/get_By_User/${userId}`, {}, 'GET')
    },
    create: function (dto: UserScannerProfile): AxiosPromise<Response> {
      return createRequest('api/UserScannerProfile/create', dto, 'POST')
    },
    update: function (dto: UserScannerProfile): AxiosPromise<Response> {
      return createRequest('api/UserScannerProfile/update', dto, 'PUT')
    },
    delete: function (id: number): AxiosPromise<Response> {
      return createRequest(`api/UserScannerProfile/delete/${id}`, {}, 'DELETE')
    }
  },

  // ------------------- User Scanner Option -------------------
  userScannerOption: {
    get_By_Profile: function (profileId: number): AxiosPromise<Response> {
      return createRequest(`api/UserScannerOption/get_By_Profile/${profileId}`, {}, 'GET')
    },
    create: function (dto: UserScannerOption): AxiosPromise<Response> {
      return createRequest('api/UserScannerOption/create', dto, 'POST')
    },
    update: function (dto: UserScannerOption): AxiosPromise<Response> {
      return createRequest('api/UserScannerOption/update', dto, 'PUT')
    },
    delete: function (id: number): AxiosPromise<Response> {
      return createRequest(`api/UserScannerOption/delete/${id}`, {}, 'DELETE')
    }
  }
}
